import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Navbar, MobileNav } from "@/components/Navbar";
import { api, fileUrl, formatFollowers, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CreatorBadges, GiftedTag } from "@/components/Badges";
import { ReportReviewButton } from "@/components/ReportReviewButton";
import { PLATFORM_META } from "@/components/SocialIcons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Stars } from "@/components/Rating";
import { InviteToOpportunity } from "@/components/InviteToOpportunity";
import { ConnectButton } from "@/components/ConnectButton";
import { ShareButton } from "@/components/ShareButton";
import { creatorShareUrl } from "@/lib/share";
import { SocialEmbed, detectPlatform } from "@/components/SocialEmbed";
import { MapPin, Eye, TrendingUp, ExternalLink, Loader2, Send, Play, BadgeCheck } from "lucide-react";
import { toast } from "sonner";

function PortfolioItem({ item, onOpen }) {
  const isVideo = item.type === "video";
  const isLink = item.type === "link";
  const src = fileUrl(item.thumbnail || item.url);
  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      data-testid={`portfolio-item-${item.id}`}
      className="group relative aspect-square overflow-hidden rounded-xl border border-zinc-200 bg-zinc-100"
    >
      {isVideo ? (
        <video src={fileUrl(item.url)} className="h-full w-full object-cover" muted />
      ) : isLink && !item.thumbnail ? (
        <div className="flex h-full w-full flex-col items-center justify-center bg-gradient-to-br from-zinc-100 to-zinc-200 p-3 text-center">
          <ExternalLink className="h-6 w-6 text-zinc-500" />
          <span className="mt-2 text-xs font-medium capitalize text-zinc-600 line-clamp-2">{item.caption || item.platform || "View post"}</span>
        </div>
      ) : (
        <img src={src} alt={item.caption || "content"} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
      )}
      {(isVideo || isLink) && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/90"><Play className="h-4 w-4 fill-zinc-900 text-zinc-900" /></div>
        </div>
      )}
      {item.caption && !isLink && (
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2">
          <p className="text-xs font-medium text-white line-clamp-1">{item.caption}</p>
        </div>
      )}
    </button>
  );
}

function RequestCollabForm({ creator, onDone }) {
  const [meta, setMeta] = useState({});
  const [form, setForm] = useState({
    campaign_title: "", product_service: "", platform: "instagram", content_type: "",
    proposed_payment: "", gift_offered: "", posting_date: "", brief: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/collaborations", {
        creator_user_id: creator.user_id,
        ...form,
        proposed_payment: form.proposed_payment ? Number(form.proposed_payment) : null,
      });
      toast.success("Collaboration request sent!");
      onDone();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-4 space-y-4">
      <div>
        <Label>Campaign title</Label>
        <Input data-testid="collab-title" required value={form.campaign_title}
          onChange={(e) => setForm({ ...form, campaign_title: e.target.value })} className="mt-1.5" placeholder="Summer menu launch" />
      </div>
      <div>
        <Label>Product / service being promoted</Label>
        <Input data-testid="collab-product" value={form.product_service}
          onChange={(e) => setForm({ ...form, product_service: e.target.value })} className="mt-1.5" placeholder="New brunch menu" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Platform</Label>
          <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
            <SelectTrigger data-testid="collab-platform" className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="instagram">Instagram</SelectItem>
              <SelectItem value="tiktok">TikTok</SelectItem>
              <SelectItem value="youtube">YouTube</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Content type</Label>
          <Select value={form.content_type} onValueChange={(v) => setForm({ ...form, content_type: v })}>
            <SelectTrigger data-testid="collab-content-type" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              {meta.services?.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Proposed payment (£)</Label>
          <Input data-testid="collab-payment" type="number" value={form.proposed_payment}
            onChange={(e) => setForm({ ...form, proposed_payment: e.target.value })} className="mt-1.5" placeholder="75" />
        </div>
        <div>
          <Label>Posting date</Label>
          <Input data-testid="collab-date" type="date" value={form.posting_date}
            onChange={(e) => setForm({ ...form, posting_date: e.target.value })} className="mt-1.5" />
        </div>
      </div>
      <div>
        <Label>Gift / product / experience offered</Label>
        <Input data-testid="collab-gift" value={form.gift_offered}
          onChange={(e) => setForm({ ...form, gift_offered: e.target.value })} className="mt-1.5" placeholder="Free meal for two" />
      </div>
      <div>
        <Label>Campaign brief / message</Label>
        <Textarea data-testid="collab-brief" value={form.brief} rows={4}
          onChange={(e) => setForm({ ...form, brief: e.target.value })} className="mt-1.5" placeholder="Tell the creator what you're looking for…" />
      </div>
      {error && <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-600">{error}</p>}
      <Button type="submit" data-testid="collab-submit" disabled={busy} className="h-11 w-full rounded-full bg-emerald-600 hover:bg-emerald-700">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Send className="mr-2 h-4 w-4" /> Send request</>}
      </Button>
    </form>
  );
}

export default function CreatorProfilePublic() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [reviews, setReviews] = useState({ reviews: [], average: 0, count: 0 });
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get(`/creators/${id}`).then((r) => setCreator(r.data)).catch(() => setCreator(null)).finally(() => setLoading(false));
    api.get(`/creators/${id}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  }, [id]);

  if (loading) return <div className="flex h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;
  if (!creator) return <div className="p-10 text-center text-zinc-500">Creator not found.</div>;

  const socials = creator.socials || {};
  const activePlatforms = Object.keys(socials).filter((p) => socials[p] && (socials[p].username || socials[p].followers));
  const services = (creator.services || []).filter((s) => s.type);
  const isBusiness = user?.role === "business";

  const RequestButton = (
    <Button data-testid="request-collab-btn" onClick={() => setOpen(true)} className="h-12 w-full rounded-full bg-emerald-600 text-base hover:bg-emerald-700">
      <Send className="mr-2 h-5 w-5" /> Request Collaboration
    </Button>
  );

  return (
    <div className="min-h-screen bg-[#FAF9F6] nf-pb-action">
      <Navbar />
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>Request a collaboration with {creator.display_name}</SheetTitle>
            <SheetDescription>Send {creator.display_name} a campaign opportunity. They can accept, decline or message you back.</SheetDescription>
          </SheetHeader>
          <RequestCollabForm creator={creator} onDone={() => { setOpen(false); navigate("/dashboard"); }} />
        </SheetContent>
      </Sheet>

      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-lg overflow-hidden p-0" data-testid="portfolio-lightbox">
          <DialogTitle className="sr-only">Portfolio content preview</DialogTitle>
          {lightbox && (
            <div className="max-h-[80vh] overflow-y-auto">
              {lightbox.type === "link" ? (
                <div className="p-4">
                  <SocialEmbed platform={lightbox.platform || detectPlatform(lightbox.url)} url={lightbox.url} />
                  <a href={lightbox.url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-emerald-600 hover:underline">
                    Open original <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
              ) : lightbox.type === "video" ? (
                <video src={fileUrl(lightbox.url)} controls autoPlay className="max-h-[80vh] w-full bg-black" />
              ) : (
                <img src={fileUrl(lightbox.url)} alt={lightbox.caption || "content"} className="w-full object-contain" />
              )}
              {lightbox.caption && lightbox.type !== "link" && <p className="p-4 text-sm text-zinc-600">{lightbox.caption}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
        {!user && (
          <div data-testid="creator-join-banner" className="mb-6 flex flex-col items-start justify-between gap-3 rounded-2xl bg-zinc-900 p-5 text-white sm:flex-row sm:items-center">
            <div>
              <p className="font-display font-bold">Discover local creators like {creator.display_name} on Nearfluence</p>
              <p className="mt-0.5 text-sm text-zinc-300">Local creators. Local businesses. Real influence.</p>
            </div>
            <Button data-testid="creator-join-btn" onClick={() => navigate("/auth?mode=register")} className="h-11 shrink-0 rounded-full bg-emerald-500 hover:bg-emerald-600">Join Nearfluence</Button>
          </div>
        )}
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Left column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header card */}
            <div className="rounded-3xl border border-zinc-200 bg-white p-6">
              <div className="flex flex-col items-start gap-4 sm:flex-row">
                <div className="h-24 w-24 shrink-0 overflow-hidden rounded-2xl bg-zinc-100">
                  {creator.profile_photo ? (
                    <img src={fileUrl(creator.profile_photo)} alt={creator.display_name} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-emerald-100 to-indigo-100 text-3xl font-display font-bold text-emerald-700">{creator.display_name?.[0]}</div>
                  )}
                </div>
                <div className="flex-1">
                  <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-900">{creator.display_name}</h1>
                  <p className="mt-0.5 flex items-center gap-1 text-sm text-zinc-500"><MapPin className="h-4 w-4" /> {creator.location || "UK"}</p>
                  {reviews.count > 0 && (
                    <div className="mt-1.5 flex items-center gap-1.5" data-testid="profile-rating">
                      <Stars value={reviews.average} size={16} />
                      <span className="text-sm font-semibold text-zinc-700">{reviews.average}</span>
                      <span className="text-sm text-zinc-400">({reviews.count} review{reviews.count !== 1 ? "s" : ""})</span>
                    </div>
                  )}
                  <div className="mt-3"><CreatorBadges badges={creator.badges} /></div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {creator.niches?.map((n) => <span key={n} className="rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs font-medium text-zinc-600">{n}</span>)}
                    {creator.gifted_ok && <GiftedTag />}
                  </div>
                  {user?.role === "creator" && user?.id !== creator.user_id && (
                    <div className="mt-3" data-testid="profile-connect">
                      <ConnectButton userId={creator.user_id} creatorName={creator.display_name} />
                    </div>
                  )}
                </div>
                <ShareButton testid="creator-profile-share" url={creatorShareUrl(creator.user_id)}
                  title={`${creator.display_name} on Nearfluence`}
                  text={`Check out ${creator.display_name} on Nearfluence — ${(creator.niches || []).slice(0, 2).join(", ")}${creator.location ? ", " + creator.location : ""}.`}
                  recordKind="creator_profile" recordId={creator.user_id} label="Share profile" className="mt-1 shrink-0" />
              </div>
              {creator.bio && <p className="mt-4 text-zinc-600">{creator.bio}</p>}
              {creator.areas_covered?.length > 0 && (
                <p className="mt-3 text-sm text-zinc-500"><span className="font-medium text-zinc-700">Areas covered:</span> {creator.areas_covered.join(", ")}</p>
              )}
            </div>

            {/* Verified connected socials (official API) */}
            {(creator.verified_socials || []).length > 0 && (
              <div data-testid="verified-socials-block" className="rounded-3xl border border-emerald-200 bg-emerald-50/40 p-6">
                <h2 className="flex items-center gap-2 font-display text-lg font-bold text-zinc-900">
                  <BadgeCheck className="h-5 w-5 text-emerald-600" /> Verified connected accounts
                </h2>
                <p className="mt-1 text-sm text-zinc-500">Imported directly from the platform — not self-reported.</p>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  {creator.verified_socials.map((c) => (
                    <div key={c.id} data-testid={`verified-social-${c.platform}`} className="rounded-2xl border border-emerald-200 bg-white p-4">
                      <div className="flex items-center gap-2">
                        <span className="font-display font-semibold capitalize text-zinc-900">{c.platform}</span>
                        <BadgeCheck className="h-4 w-4 text-emerald-600" />
                        {c.username && <span className="truncate text-xs text-zinc-500">@{c.username}</span>}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm">
                        {c.followers != null && <span><b className="font-mono">{formatFollowers(c.followers)}</b> <span className="text-zinc-500">followers</span></span>}
                        {c.metrics?.engagement_rate != null && <span className="text-emerald-700"><b>{c.metrics.engagement_rate}%</b> engagement</span>}
                        {c.metrics?.avg_views != null && <span><b className="font-mono">{formatFollowers(c.metrics.avg_views)}</b> <span className="text-zinc-500">avg views</span></span>}
                        {c.metrics?.avg_likes != null && <span><b className="font-mono">{formatFollowers(c.metrics.avg_likes)}</b> <span className="text-zinc-500">avg likes</span></span>}
                        {c.media_count != null && <span><b className="font-mono">{formatFollowers(c.media_count)}</b> <span className="text-zinc-500">{c.platform === "tiktok" ? "videos" : "posts"}</span></span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Social stats */}
            {activePlatforms.length > 0 && (
              <div className="rounded-3xl border border-zinc-200 bg-white p-6">
                <h2 className="font-display text-lg font-bold text-zinc-900">Social platforms</h2>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  {activePlatforms.map((p) => {
                    const acc = socials[p];
                    const pm = PLATFORM_META[p];
                    if (!pm) return null;
                    const { Icon, label, color } = pm;
                    const connected = acc.source === "connected";
                    return (
                      <div key={p} className="rounded-2xl border border-zinc-200 p-4">
                        <div className="flex items-center gap-2">
                          <Icon className={`h-5 w-5 ${color}`} />
                          <span className="font-semibold text-zinc-900">{label}</span>
                          <span data-testid={`public-social-${p}-source`} className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${connected ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>
                            {connected ? "Verified" : "Self-reported"}
                          </span>
                          {acc.url && <a href={acc.url} target="_blank" rel="noopener noreferrer" className="ml-auto text-zinc-400 hover:text-emerald-600"><ExternalLink className="h-4 w-4" /></a>}
                        </div>
                        {acc.username && <p className="mt-1 text-sm text-zinc-500">@{acc.username}</p>}
                        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                          <div><p className="font-mono text-lg font-bold text-zinc-900">{formatFollowers(acc.followers)}</p><p className="text-xs text-zinc-400">Followers</p></div>
                          <div><p className="font-mono text-lg font-bold text-zinc-900">{formatFollowers(acc.avg_views)}</p><p className="text-xs text-zinc-400">Avg views</p></div>
                          <div><p className="font-mono text-lg font-bold text-emerald-600">{acc.engagement || 0}%</p><p className="text-xs text-zinc-400">Engage</p></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Portfolio */}
            <div className="rounded-3xl border border-zinc-200 bg-white p-6">
              <h2 className="font-display text-lg font-bold text-zinc-900">Recent content</h2>
              {creator.portfolio?.length > 0 ? (
                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {creator.portfolio.map((item) => <PortfolioItem key={item.id} item={item} onOpen={setLightbox} />)}
                </div>
              ) : (
                <p className="mt-4 text-sm text-zinc-400">This creator hasn't added portfolio content yet.</p>
              )}
            </div>

            {/* Reviews */}
            <div className="rounded-3xl border border-zinc-200 bg-white p-6" data-testid="reviews-section">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-lg font-bold text-zinc-900">Reviews</h2>
                {reviews.count > 0 && (
                  <div className="flex items-center gap-1.5">
                    <Stars value={reviews.average} size={16} />
                    <span className="text-sm font-semibold text-zinc-700">{reviews.average} · {reviews.count}</span>
                  </div>
                )}
              </div>
              {reviews.reviews.length > 0 ? (
                <div className="mt-4 space-y-4">
                  {reviews.reviews.map((r) => (
                    <div key={r.id} data-testid={`review-${r.id}`} className="rounded-2xl border border-zinc-100 bg-zinc-50/60 p-4">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-zinc-900">{r.reviewer_name || r.business_name}</p>
                          {r.verified && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Verified collaboration</span>}
                        </div>
                        <Stars value={r.rating} size={14} />
                      </div>
                      {r.created_at && <p className="mt-0.5 text-xs text-zinc-400">{new Date(r.created_at).toLocaleDateString()}</p>}
                      {r.comment && <p className="mt-2 text-sm text-zinc-600">{r.comment}</p>}
                      <ReportReviewButton reviewId={r.id} />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-4 text-sm text-zinc-400">No reviews yet — be the first to collaborate.</p>
              )}
            </div>
          </div>

          {/* Right column - services + CTA */}
          <div className="space-y-6">
            <div className="rounded-3xl border border-zinc-200 bg-white p-6 lg:sticky lg:top-24">
              <h2 className="font-display text-lg font-bold text-zinc-900">Collaboration options</h2>
              {services.length > 0 ? (
                <div className="mt-4 space-y-2.5">
                  {services.map((s, i) => (
                    <div key={i} className="flex items-center justify-between rounded-xl bg-zinc-50 px-3 py-2.5">
                      <span className="text-sm font-medium text-zinc-700">{s.type}</span>
                      <span className="font-display font-bold text-emerald-600">{s.price_from != null && s.price_from !== "" ? `From £${s.price_from}` : "POA"}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-3 text-sm text-zinc-400">Contact for pricing.</p>
              )}
              {creator.gifted_ok && (
                <div className="mt-3 flex items-center gap-2 rounded-xl bg-indigo-50 px-3 py-2.5 text-sm text-indigo-700">
                  🎁 Open to gifted collaborations
                </div>
              )}
              <div className="mt-5 hidden lg:block">
                {!user ? (
                  <Button data-testid="request-collab-btn-login" onClick={() => navigate("/auth?mode=register&role=business")} className="h-12 w-full rounded-full bg-emerald-600 text-base hover:bg-emerald-700">
                    Sign up to collaborate
                  </Button>
                ) : isBusiness ? (
                  <div className="space-y-2">
                    {RequestButton}
                    <InviteToOpportunity creatorUserId={creator.user_id} creatorName={creator.display_name}
                      trigger={<Button data-testid="invite-to-opp-btn" variant="outline" className="h-11 w-full rounded-full"><Send className="mr-2 h-4 w-4" /> Invite to Opportunity</Button>} />
                  </div>
                ) : (
                  <p className="rounded-xl bg-zinc-50 p-3 text-center text-sm text-zinc-500">Only business accounts can request collaborations.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sticky CTA */}
      <div className={`${user ? "nf-actionbar-abovenav" : "nf-actionbar-bottom"} border-t border-zinc-200 bg-white p-3 md:hidden`}>
        {!user ? (
          <Button data-testid="request-collab-btn-mobile" onClick={() => navigate("/auth?mode=register&role=business")} className="h-12 w-full rounded-full bg-emerald-600 text-base hover:bg-emerald-700">Sign up to collaborate</Button>
        ) : isBusiness ? (
          <Button data-testid="request-collab-btn-mobile-open" onClick={() => setOpen(true)} className="h-12 w-full rounded-full bg-emerald-600 text-base hover:bg-emerald-700">
            <Send className="mr-2 h-5 w-5" /> Request Collaboration
          </Button>
        ) : (
          <p className="text-center text-sm text-zinc-500">Only business accounts can request collaborations.</p>
        )}
      </div>
      <MobileNav />
    </div>
  );
}
